import { router, usePage } from '@inertiajs/react'
import { ChevronDown, ChevronRight, Download, SlidersHorizontal, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  reportsInstrumentsPath,
  reportsInstrumentsCsvPath,
  reportsInstrumentsPdfPath,
} from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebReportInstrumentRow from '@/shared/types/reports/WebReportInstrumentRow'
import { ReportEmptyState } from '../components/ReportEmptyState'
import { ReportMobileField } from '../components/ReportMobileField'
import { ReportRangeOrderError } from '../components/ReportRangeOrderError'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import {
  buildOrgCascadeChangeHandlers,
  visibleDepartmentOptionsForSites,
} from '@/shared/lib/orgMultiSelectCascade'
import type { SharedProps } from '@/shared/types/SharedProps'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { clearLocationFilters, mergeLocationFilters } from '@/shared/lib/locationFiltersSession'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import { downloadExport } from '@/shared/lib/downloadExport'

interface FiltersShape {
  fromDate?: string
  toDate?: string
  instrumentTypeIds?: number[]
  hideEmpty?: boolean
  siteIds?: number[]
  departmentIds?: number[]
}

interface NamedOption {
  id: number
  name: string
}

interface OrgOption {
  value: number
  label: string
  siteId?: number
}

interface Props {
  rows: WebReportInstrumentRow[]
  meta: PaginationMeta
  filters: FiltersShape
  defaultFromDate: string
  defaultToDate: string
  instrumentTypeOptions: NamedOption[]
  siteOptions: OrgOption[]
  departmentOptions: OrgOption[]
  [key: string]: unknown
}

const NO_TYPE_KEY = '__none__'

const InstrumentsReportPage = () => {
  const {
    rows,
    meta,
    filters,
    defaultFromDate,
    defaultToDate,
    instrumentTypeOptions,
    siteOptions,
    departmentOptions,
    complianceSettings,
    currentTenantId,
    currentUserInfo,
  } = usePage<Props & SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null

  useBreadcrumbs([{ label: t('nav.instrumentsReport') }])

  const [fromDate, setFromDate] = useState(filters.fromDate ?? defaultFromDate)
  const [toDate, setToDate] = useState(filters.toDate ?? defaultToDate)
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(() =>
    (filters.siteIds ?? []).map((id) => Number(id)),
  )
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>(() =>
    (filters.departmentIds ?? []).map((id) => Number(id)),
  )
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>(filters.instrumentTypeIds ?? [])
  const [hideEmpty, setHideEmpty] = useState(filters.hideEmpty ?? false)
  const [expandedTypeKeys, setExpandedTypeKeys] = useState<Set<string>>(new Set())
  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()

  /* eslint-disable react-hooks/set-state-in-effect -- mirror URL-driven Inertia props (camelCase) */
  useEffect(() => {
    setFromDate(filters.fromDate ?? defaultFromDate)
    setToDate(filters.toDate ?? defaultToDate)
    setSelectedSiteIds((filters.siteIds ?? []).map((id) => Number(id)))
    setSelectedDepartmentIds((filters.departmentIds ?? []).map((id) => Number(id)))
    setSelectedTypeIds(filters.instrumentTypeIds ?? [])
    setHideEmpty(filters.hideEmpty ?? false)
  }, [
    filters.fromDate,
    filters.toDate,
    filters.siteIds,
    filters.departmentIds,
    filters.instrumentTypeIds,
    filters.hideEmpty,
    defaultFromDate,
    defaultToDate,
  ])
  /* eslint-enable react-hooks/set-state-in-effect */

  const dateRangeInvalid = isReportRangeOrderInvalid(fromDate, toDate)

  const buildFilterParams = (
    overrides: {
      fromDate?: string
      toDate?: string
      instrumentTypeIds?: number[]
      hideEmpty?: boolean
      siteIds?: number[]
      departmentIds?: number[]
    } = {},
  ) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    const tids = overrides.instrumentTypeIds ?? selectedTypeIds
    const he = overrides.hideEmpty ?? hideEmpty
    const siteIds = overrides.siteIds ?? selectedSiteIds
    const departmentIds = overrides.departmentIds ?? selectedDepartmentIds
    const params: Record<string, string> = {}
    if (siteIds.length > 0) params.site_ids = siteIds.join(',')
    if (departmentIds.length > 0) params.department_ids = departmentIds.join(',')
    if (fd) params.from_date = fd
    if (td) params.to_date = td
    if (tids.length > 0) params.instrument_type_ids = tids.join(',')
    if (he) params.hide_empty = '1'
    return params
  }

  const navigate = (
    overrides: Parameters<typeof buildFilterParams>[0] = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    if (isReportRangeOrderInvalid(fd, td)) return
    mergeLocationFilters(currentTenantId, currentUserId, {
      siteIds: overrides.siteIds ?? selectedSiteIds,
      departmentIds: overrides.departmentIds ?? selectedDepartmentIds,
    })
    const params = buildFilterParams(overrides)
    const visit = beginVisit(
      JSON.stringify(params),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    router.get(reportsInstrumentsPath(), params, {
      preserveState: visit.keepDropdownOpen,
      ...(visit.keepDropdownOpen ? { preserveScroll: true } : {}),
    })
  }

  const handleFromDateChange = (val: string) => {
    setFromDate(val)
    navigate({ fromDate: val })
  }

  const handleToDateChange = (val: string) => {
    setToDate(val)
    navigate({ toDate: val })
  }

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions ?? [], selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  // This report has no Unit filter, so the unit slot stays empty.
  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions: departmentOptions ?? [],
    unitOptions: [],
    selection: { siteIds: selectedSiteIds, departmentIds: selectedDepartmentIds, unitIds: [] },
    applySelection: ({ siteIds, departmentIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDepartmentIds(departmentIds)
    },
  })

  const handleSiteIdsCommit = (selected: number[]) =>
    navigate(orgCascade.changeSites(selected), 'commit')

  const handleDepartmentIdsCommit = (selected: number[]) =>
    navigate(orgCascade.changeDepartments(selected), 'commit')

  const closeSheetWithCascadePrune = () => navigate(orgCascade.pruneSelection(), 'sheet-close')

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    syncMode: 'merge',
    hasUrlLocationParams:
      (filters.siteIds?.length ?? 0) > 0 || (filters.departmentIds?.length ?? 0) > 0,
    currentFilters: {
      siteIds: (filters.siteIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
      departmentIds: (filters.departmentIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
      unitIds: [],
    },
    siteOptions,
    departmentOptions,
    unitOptions: [],
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDepartmentIds(hydrated.departmentIds)
      navigate({ siteIds: hydrated.siteIds, departmentIds: hydrated.departmentIds })
    },
  })

  const handleTypeIdsCommit = (selected: number[]) => {
    setSelectedTypeIds(selected)
    navigate({ instrumentTypeIds: selected }, 'commit')
  }

  const handleHideEmptyChange = (checked: boolean) => {
    setHideEmpty(checked)
    navigate({ hideEmpty: checked })
  }

  const clearFilters = () => {
    setFromDate(defaultFromDate)
    setToDate(defaultToDate)
    setSelectedSiteIds([])
    setSelectedDepartmentIds([])
    setSelectedTypeIds([])
    setHideEmpty(false)
    clearLocationFilters(currentTenantId, currentUserId)
    router.get(reportsInstrumentsPath(), {}, { preserveState: false })
  }

  const handlePageChange = (page: number) => {
    if (dateRangeInvalid) return
    const params = { ...buildFilterParams(), page: String(page) }
    const visit = beginVisit(JSON.stringify(params), 'default')
    if (!visit) return
    router.get(reportsInstrumentsPath(), params, { preserveState: false })
  }

  const handleExportCsv = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsInstrumentsCsvPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const handleExportPdf = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsInstrumentsPdfPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  const toggleType = (typeKey: string) => {
    setExpandedTypeKeys((prev) => {
      const next = new Set(prev)
      if (next.has(typeKey)) {
        next.delete(typeKey)
      } else {
        next.add(typeKey)
      }
      return next
    })
  }

  const activeFilterCount = [
    selectedSiteIds.length > 0,
    selectedDepartmentIds.length > 0,
    fromDate !== defaultFromDate,
    toDate !== defaultToDate,
    selectedTypeIds.length > 0,
    hideEmpty,
  ].filter(Boolean).length

  const groupedRows = useMemo(() => {
    const groups = new Map<string, WebReportInstrumentRow[]>()
    for (const row of rows) {
      // Group by type id, not name: two types may share a name, and the server pools the
      // type score per type id, so a name-group could mix rows from different types.
      const key = row.instrumentTypeId == null ? NO_TYPE_KEY : `type-${row.instrumentTypeId}`
      const existing = groups.get(key)
      if (existing) {
        existing.push(row)
      } else {
        groups.set(key, [row])
      }
    }
    return groups
  }, [rows])

  const typeGroupName = (groupRows: WebReportInstrumentRow[]): string =>
    groupRows[0]?.instrumentTypeName ?? t('reports.instruments.typeGroupUnknown')

  // Pooled on the server across every instrument of the type (all pages); never re-averaged here.
  const typeGroupCompliance = (groupRows: WebReportInstrumentRow[]): number | null =>
    groupRows[0]?.typeCompliancePercent ?? null

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-start">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.instruments.filterSite')}
        </Label>
        <MultiSelectCombobox
          options={(siteOptions ?? []).map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedSiteIds}
          onChange={orgCascade.changeSites}
          onClose={handleSiteIdsCommit}
          placeholder={t('reports.instruments.filterSitePlaceholder')}
          className="w-full lg:w-auto lg:min-w-40 lg:max-w-64"
          showBadges={false}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.instruments.filterDepartment')}
        </Label>
        <MultiSelectCombobox
          options={filteredDepartmentOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedDepartmentIds}
          onChange={orgCascade.changeDepartments}
          onClose={handleDepartmentIdsCommit}
          placeholder={t('reports.instruments.filterDepartmentPlaceholder')}
          className="w-full lg:w-auto lg:min-w-40 lg:max-w-64"
          showBadges={false}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.instruments.fromDate')}</Label>
        <DatePickerInput
          value={fromDate}
          onChange={handleFromDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.instruments.toDate')}</Label>
        <DatePickerInput
          value={toDate}
          onChange={handleToDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.instruments.filterInstrumentType')}
        </Label>
        <MultiSelectCombobox
          options={instrumentTypeOptions.map((typeOption) => ({
            value: typeOption.id,
            label: typeOption.name,
          }))}
          selected={selectedTypeIds}
          onChange={setSelectedTypeIds}
          onClose={handleTypeIdsCommit}
          placeholder={t('reports.instruments.filterInstrumentTypePlaceholder')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>
      <div className="flex items-center gap-2 self-end pb-1">
        <Checkbox
          id="hide-empty-instruments"
          checked={hideEmpty}
          onCheckedChange={(checkedValue) => handleHideEmptyChange(checkedValue === true)}
        />
        <Label htmlFor="hide-empty-instruments" className="cursor-pointer text-sm">
          {t('reports.instruments.hideEmpty')}
        </Label>
      </div>
      <ReportRangeOrderError show={dateRangeInvalid} />
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="min-w-0 text-xl font-bold sm:text-2xl">{t('reports.instruments.title')}</h1>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.instruments.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPdf}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.instruments.exportPdf')}
          </Button>

          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                // Prune on close — MultiSelect onClose may not run (e.g. Escape).
                if (!open) closeSheetWithCascadePrune()
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('reports.instruments.filters')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('reports.instruments.filters')}
                hideCloseButton
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-semibold">{t('reports.instruments.filters')}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('reports.instruments.clearFilters')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('reports.instruments.filters')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {filterFields}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('reports.instruments.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <ReportEmptyState message={t('reports.instruments.noRecords')} />
      ) : (
        <div className="space-y-4">
          {/* Mobile cards */}
          <div className="space-y-2 lg:hidden">
            {Array.from(groupedRows.entries()).map(([typeKey, groupRows]) => {
              const typeName = typeGroupName(groupRows)
              const expanded = expandedTypeKeys.has(typeKey)
              return (
                <div key={typeKey} className="rounded-lg border bg-card shadow-sm">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-3 text-left"
                    onClick={() => toggleType(typeKey)}
                    aria-label={t('reports.instruments.expandType')}
                  >
                    <div>
                      <p className="font-semibold text-foreground">{typeName}</p>
                      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                        <span>
                          {groupRows.length} {t('reports.instruments.paginationNoun')}
                        </span>
                        <span aria-hidden="true">·</span>
                        <ComplianceBadge
                          percent={typeGroupCompliance(groupRows)}
                          colorCodes={complianceSettings?.colorCodes}
                        />
                      </p>
                    </div>
                    {expanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </button>
                  {expanded && (
                    <div className="space-y-2 border-t px-3 pb-3 pt-2">
                      {groupRows.map((row) => (
                        <div
                          key={[row.id, row.unitName, row.departmentName].join('|')}
                          className="rounded bg-muted/40 p-2"
                        >
                          <p className="font-medium [overflow-wrap:anywhere]">
                            {row.instrumentLabel}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {row.serialNumber ? `S/N: ${row.serialNumber}` : null}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {[row.departmentName, row.unitName, row.siteName]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </p>
                          <div className="mt-2">
                            <ReportMobileField label={t('reports.instruments.colAvgCompliance')}>
                              <ComplianceBadge
                                percent={row.avgCompliancePercent}
                                colorCodes={complianceSettings?.colorCodes}
                              />
                            </ReportMobileField>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>{t('reports.instruments.colInstrument')}</TableHead>
                    <TableHead>{t('reports.instruments.colSerial')}</TableHead>
                    <TableHead>{t('reports.instruments.colDepartment')}</TableHead>
                    <TableHead>{t('reports.instruments.colUnit')}</TableHead>
                    <TableHead>{t('reports.instruments.colSite')}</TableHead>
                    <TableHead className="text-right">
                      {t('reports.instruments.colAvgCompliance')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from(groupedRows.entries()).map(([typeKey, groupRows]) => {
                    const typeName = typeGroupName(groupRows)
                    const expanded = expandedTypeKeys.has(typeKey)
                    return (
                      <Collapsible
                        key={typeKey}
                        open={expanded}
                        onOpenChange={() => toggleType(typeKey)}
                        asChild
                      >
                        <>
                          <TableRow className="cursor-pointer bg-muted/20 hover:bg-muted/40">
                            <TableCell className="py-2">
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                  {expanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            </TableCell>
                            <TableCell
                              colSpan={5}
                              className="font-semibold"
                              onClick={() => toggleType(typeKey)}
                            >
                              {typeName}
                              <span className="ml-2 text-sm font-normal text-muted-foreground">
                                ({groupRows.length})
                              </span>
                            </TableCell>
                            <TableCell
                              className="text-right font-medium"
                              onClick={() => toggleType(typeKey)}
                            >
                              <ComplianceBadge
                                percent={typeGroupCompliance(groupRows)}
                                colorCodes={complianceSettings?.colorCodes}
                              />
                            </TableCell>
                          </TableRow>
                          <CollapsibleContent asChild>
                            <>
                              {groupRows.map((row) => (
                                <TableRow
                                  key={[row.id, row.unitName, row.departmentName].join('|')}
                                >
                                  <TableCell />
                                  <TableCell className="font-medium">
                                    {row.instrumentLabel}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {row.serialNumber ?? '—'}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {row.departmentName ?? '—'}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {row.unitName ?? '—'}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {row.siteName ?? '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <ComplianceBadge
                                      percent={row.avgCompliancePercent}
                                      colorCodes={complianceSettings?.colorCodes}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <DataPagination
            meta={meta}
            onPageChange={handlePageChange}
            noun={t('reports.instruments.paginationNoun')}
          />
        </div>
      )}
    </div>
  )
}

export default InstrumentsReportPage
